import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Lock, Send, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputStyle = {
    background: "rgba(255,253,250,0.9)",
    border: "1px solid rgba(212,175,55,0.4)",
    color: "var(--ink)",
};

export default function RsvpSection() {
    const [name, setName] = useState("");
    const [attendance, setAttendance] = useState("attending");
    const [guestsCount, setGuestsCount] = useState(2);
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const submitRsvp = async (e) => {
        e.preventDefault();
        if (name.trim().length < 2) {
            toast.error("Please enter your name");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/rsvp`, {
                name: name.trim(),
                attendance,
                guests: attendance === "attending" ? guestsCount : 0,
                message: message.trim() || null,
            });
            toast.success("Thank you! Your RSVP has been received with love.");
            setName("");
            setMessage("");
            setGuestsCount(2);
            setAttendance("attending");
        } catch {
            toast.error("Could not send your RSVP. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="rsvp" data-testid="rsvp-section" className="mx-auto max-w-5xl px-6 py-20 sm:py-28">
            <Reveal className="text-center">
                <p className="eyebrow">04 · Your presence</p>
                <h2 className="font-display mt-3 text-3xl tracking-tight sm:text-4xl lg:text-5xl" style={{ color: "var(--ink)" }}>
                    Will you join <span className="italic" style={{ color: "var(--wine)" }}>our celebration?</span>
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm sm:text-base" style={{ color: "var(--muted)" }}>
                    Please let the family know if you'll be celebrating with us. We can't wait to share these moments with you.
                </p>
            </Reveal>

            <Reveal className="mx-auto mt-14 max-w-xl">
                <form
                    onSubmit={submitRsvp}
                    data-testid="rsvp-form"
                    className="gold-ring flex flex-col gap-6 rounded-3xl p-8 sm:p-10"
                    style={{ background: "rgba(255,253,250,0.85)" }}
                >
                    <div>
                        <label htmlFor="rsvp-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--wine)" }}>
                            Your name
                        </label>
                        <input
                            id="rsvp-name"
                            data-testid="rsvp-name-input"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Ramesh Kumar & family"
                            className="w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2"
                            style={inputStyle}
                            maxLength={80}
                            required
                        />
                    </div>

                    <div>
                        <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--wine)" }}>
                            Will you be joining us?
                        </span>
                        <div className="flex gap-3">
                            {[
                                { value: "attending", label: "Joyfully, yes" },
                                { value: "spirit", label: "In spirit" },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    data-testid={`rsvp-attendance-${opt.value}`}
                                    onClick={() => setAttendance(opt.value)}
                                    className="flex-1 rounded-full px-4 py-3 text-sm font-semibold transition-colors duration-300"
                                    style={{
                                        background: attendance === opt.value ? "var(--wine)" : "transparent",
                                        color: attendance === opt.value ? "var(--ivory)" : "var(--wine)",
                                        border: "1px solid var(--wine)",
                                    }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <AnimatePresence initial={false}>
                        {attendance === "attending" && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.35 }}
                                className="overflow-hidden"
                            >
                                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--wine)" }}>
                                    Number of guests
                                </span>
                                <div className="flex items-center gap-4">
                                    <button
                                        type="button"
                                        data-testid="rsvp-guests-minus"
                                        onClick={() => setGuestsCount((g) => Math.max(1, g - 1))}
                                        className="flex h-10 w-10 items-center justify-center rounded-full"
                                        style={{ border: "1px solid rgba(212,175,55,0.5)", color: "var(--wine)" }}
                                    >
                                        <Minus size={16} />
                                    </button>
                                    <span data-testid="rsvp-guests-count" className="font-display w-10 text-center text-3xl" style={{ color: "var(--ink)" }}>
                                        {guestsCount}
                                    </span>
                                    <button
                                        type="button"
                                        data-testid="rsvp-guests-plus"
                                        onClick={() => setGuestsCount((g) => Math.min(12, g + 1))}
                                        className="flex h-10 w-10 items-center justify-center rounded-full"
                                        style={{ border: "1px solid rgba(212,175,55,0.5)", color: "var(--wine)" }}
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div>
                        <label htmlFor="rsvp-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--wine)" }}>
                            Wishes for the couple <span style={{ color: "var(--muted)" }}>(optional)</span>
                        </label>
                        <textarea
                            id="rsvp-message"
                            data-testid="rsvp-message-input"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Blessings for the couple…"
                            rows={3}
                            maxLength={500}
                            className="w-full resize-none rounded-xl px-4 py-3 text-sm outline-none focus:ring-2"
                            style={inputStyle}
                        />
                    </div>

                    <button
                        type="submit"
                        data-testid="rsvp-submit-button"
                        disabled={sending}
                        className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                        style={{ background: "var(--wine)", color: "var(--ivory)" }}
                    >
                        <Send size={16} /> {sending ? "Sending…" : "Send my RSVP"}
                    </button>
                </form>
            </Reveal>

            <Reveal delay={0.15} className="mt-10 text-center">
                <Link
                    to="/guest-list"
                    data-testid="guest-list-link"
                    className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-xs font-semibold uppercase tracking-[0.2em] transition-transform duration-300 hover:-translate-y-0.5"
                    style={{ border: "1px solid var(--wine)", color: "var(--wine)", background: "transparent" }}
                >
                    <Lock size={14} /> Family? View the guest list
                </Link>
            </Reveal>
        </section>
    );
}
